import React from 'react'
import { FlexProps, Flex, forwardRef, SegmentedControl, Text } from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { ClientOnly } from '~/common/nextJsUtils'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'

import { Grid, List } from '~/icons'

export type DisplayMode = 'grid' | 'list'

export type DisplayModeButtonsProps = FlexProps & {
  /**
   * Uses the context value, but you can override if you need to
   */
  onChange?: (mode: DisplayMode) => void
  /**
   * Uses the context value, but you can override if you need to
   */
  value?: DisplayMode
}

const sxDisplayInPopoverOnly = { display: 'none', '[data-action-popover-content] &': { display: 'block' } }

export const DisplayModeButtons = forwardRef<DisplayModeButtonsProps, 'div'>(
  ({ onChange: propsOnChange, value: propsValue, ...props }, ref) => {
    const { displayMode: displayModeContext } = useActionBarContext()
    const value: DisplayMode = propsValue ?? displayModeContext?.value ?? 'list'
    const onChange = propsOnChange ?? displayModeContext.onChange
    const msg = useMsg()
    return (
      <Flex data-display-mode-buttons alignItems="center" gap={2} {...props}>
        <Text data-display-mode-label textStyle="bodySmall" whiteSpace="nowrap" sx={sxDisplayInPopoverOnly}>
          <Msg id="common.ui.view" description="View" />
        </Text>
        <ClientOnly>
          <SegmentedControl
            ref={ref}
            aria-label={msg('common.ui.view')}
            options={[
              {
                label: (
                  <Flex>
                    <List />
                    <Text sx={sxDisplayInPopoverOnly}>
                      <Msg id="common.ui.list" />
                    </Text>
                  </Flex>
                ),
                value: 'list',
                tooltip: msg('common.ui.list_view')
              },
              {
                label: (
                  <Flex>
                    <Grid />
                    <Text sx={sxDisplayInPopoverOnly}>
                      <Msg id="common.ui.grid" />
                    </Text>
                  </Flex>
                ),
                value: 'grid',
                tooltip: msg('common.ui.grid_view')
              }
            ]}
            value={value}
            onChange={nextValue => {
              onChange(nextValue as DisplayMode)
            }}
          />
        </ClientOnly>
      </Flex>
    )
  }
)
